import React, { useState } from 'react';
import { Autocomplete, darken, lighten, styled } from '@mui/material';
import { useAgentsQuery } from '../shared';
import { TextInput } from '@coverright/ui/inputs';
import { getUserName } from '@coverright/utils';
import { AgentOutput } from '@coverright/data-access/types/enrollment';
import _ from 'lodash';

type Agent = AgentOutput & { type: 'Agents' | 'CSRs' | 'Disabled' };

// Shared agent-fetch + row-building logic used by both the single and multi selectors,
// so the query/grouping isn't duplicated across components.
const useAgentRows = (showEmptyOption?: boolean) => {
  const { data, loading } = useAgentsQuery({
    variables: { filter: { enabled: null }, page: { page: 0, size: 1000 } },
  });

  const agents = data?.agents.data;

  const rows: Agent[] = React.useMemo(() => {
    const result: any[] = [];
    if (!agents) return result;
    if (showEmptyOption) result.push({ value: '', label: '-' });

    const active = agents.filter(a => !('enabled' in a && a.enabled === false) && !('isCSR' in a && a.isCSR));
    active.forEach(a => result.push({ ...a, type: 'Agents' }));

    const CSRs = agents.filter(a => !('enabled' in a && a.enabled === false) && 'isCSR' in a && a.isCSR);
    CSRs.forEach(a => result.push({ ...a, type: 'CSRs' }));

    const disabled = agents.filter(a => 'enabled' in a && a.enabled === false);
    disabled.forEach(a => result.push({ ...a, type: 'Disabled' }));

    return result;
  }, [agents, showEmptyOption]);

  const byId = (id?: string): Agent | null => {
    const agent = agents?.find(a => a.id === id);
    return agent ? { ...agent, type: 'Agents' } : null;
  };

  return { rows, loading, byId, ready: !!data };
};

// Props shared by the Autocomplete in both modes.
const sharedAutocompleteProps = (rows: Agent[]) => ({
  fullWidth: true,
  options: rows,
  autoHighlight: true,
  isOptionEqualToValue: (o: Agent, v: Agent) => o.id === v.id,
  groupBy: (o: Agent) => o.type,
  getOptionDisabled: (o: Agent) => o.type === 'Disabled',
  getOptionLabel: (p: Agent) => (p ? getUserName(p) : ''),
  renderGroup: (params: any) => (
    <li key={params.key}>
      <GroupHeader>{params.group}</GroupHeader>
      <GroupItems>{params.children}</GroupItems>
    </li>
  ),
});

interface AgentSelectorProps {
  onChangeAgent?: (agent?: AgentOutput) => void;
  onChange: (id: string) => void;
  value?: string;
  showEmptyOption?: boolean;
  fullWidth?: boolean;
  className?: string;
  error?: string;
  disabled?: boolean;
  hideTick?: boolean;
  label?: string;
}

// Single-select agent picker (default across the app).
export function AgentSelector(props: AgentSelectorProps) {
  const { rows, loading, byId, ready } = useAgentRows(props.showEmptyOption);
  const [value, setValue] = React.useState<Agent | null>(null);
  const [term, setTerm] = useState('');

  React.useEffect(() => {
    if (ready) setValue(byId(props.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, ready]);

  return (
    <Autocomplete
      {...sharedAutocompleteProps(rows)}
      loading={loading}
      value={value}
      onInputChange={(_e, v) => setTerm(v)}
      onChange={(_e, v) => {
        const single = v as Agent | null;
        setValue(single);
        props.onChange(single?.id || '');
        props.onChangeAgent?.(single ? _.omit(single, 'type') as AgentOutput : undefined);
      }}
      clearOnBlur
      renderInput={params => (
        <TextInput
          loading={loading}
          hideTick
          error={!!props.error}
          helperText={props.error}
          label={props.label === undefined ? 'Agent' : props.label}
          placeholder={'Enter agent name'}
          value={term}
          {...params}
        />
      )}
    />
  );
}

interface AgentMultiSelectorProps {
  onChange: (ids: string[]) => void;
  value: string[];
  label?: string;
  placeholder?: string;
  error?: string;
}

// Multi-select agent picker (Compare groups). Shares fetch/row logic with AgentSelector;
// MUI's `multiple` gives removable chips + clear for free.
export function AgentMultiSelector(props: AgentMultiSelectorProps) {
  const { rows, loading, byId, ready } = useAgentRows();
  const [value, setValue] = React.useState<Agent[]>([]);
  const [term, setTerm] = useState('');

  React.useEffect(() => {
    if (ready) setValue((props.value || []).map(byId).filter(Boolean) as Agent[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, ready]);

  return (
    <Autocomplete
      {...sharedAutocompleteProps(rows)}
      multiple
      disableCloseOnSelect
      loading={loading}
      value={value}
      onInputChange={(_e, v) => setTerm(v)}
      onChange={(_e, v) => {
        const list = v as Agent[];
        setValue(list);
        props.onChange(list.map(a => a.id));
      }}
      renderInput={params => (
        <TextInput
          loading={loading}
          hideTick
          error={!!props.error}
          helperText={props.error}
          label={props.label === undefined ? 'Agents' : props.label}
          placeholder={value.length ? '' : (props.placeholder || 'Add agents')}
          value={term}
          {...params}
        />
      )}
    />
  );
}

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  fontWeight: 600,
  fontSize: 14,
  color: darken(theme.palette.info.light, .2),
  backgroundColor: lighten(theme.palette.info.light, .9),
}));

const GroupItems = styled('ul')({
  padding: 0,
});